import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Youtube, Star } from 'lucide-react';
import { CONTACT_INFO } from '../constants/contact';

// Social & Review Links
const socialLinks = {
  yelp: 'https://www.yelp.com/biz/the-beltas-law-firm-new-york-2',
  google: 'https://www.google.com/search?q=The+Beltas+Law+Firm+New+York+reviews',
  youtube: CONTACT_INFO.youtube,
};

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary-900 text-primary-100">
      <div className="container-main py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-baseline gap-1.5 mb-4">
              <span className="font-heading text-2xl font-semibold text-white">BELTAS</span>
              <span className="font-subheading text-lg tracking-wider text-primary-300">LAW</span>
            </Link>
            <p className="font-body text-primary-200 leading-relaxed max-w-xs mb-6">
              Protecting tenants and consumers across New York, California, and Washington D.C.
            </p>

            {/* Social & Review Links */}
            <div className="flex items-center gap-4">
              <a
                href={socialLinks.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-primary-800 rounded-lg flex items-center justify-center text-primary-300 hover:text-white hover:bg-primary-700 transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a
                href={socialLinks.yelp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-primary-800 rounded-lg flex items-center justify-center text-primary-300 hover:text-white hover:bg-primary-700 transition-colors"
                aria-label="Yelp Reviews"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.14 11.28c-.21-.4-.62-.65-1.08-.64-.45 0-.86.26-1.06.66l-3.97 7.64a1.2 1.2 0 00.43 1.51c.4.27.93.26 1.32-.02l6.96-4.84c.39-.27.54-.78.36-1.22-.17-.44-.6-.72-1.06-.7-.13 0-.26.03-.38.08l-1.52-2.47zM6.1 8.28c-.2-.4-.62-.65-1.08-.64-.45 0-.86.26-1.06.66L.49 16.11a1.2 1.2 0 00.44 1.52c.4.26.93.25 1.32-.03l3.85-2.67c.39-.27.54-.78.36-1.22-.17-.44-.6-.72-1.06-.7-.13 0-.26.03-.38.08l1.08-4.81zM11.5 2.12c-.46-.01-.87.24-1.08.64l-2.5 4.81c-.18.44.02.95.46 1.18.43.22.97.08 1.25-.33l2.96-4.23c.28-.4.23-.95-.12-1.29a1.2 1.2 0 00-.97-.78zM17.9 8.28c.2-.4.62-.65 1.08-.64.45 0 .86.26 1.06.66l3.47 7.81a1.2 1.2 0 01-.44 1.52c-.4.26-.93.25-1.32-.03l-3.85-2.67c-.39-.27-.54-.78-.36-1.22.17-.44.6-.72 1.06-.7.13 0 .26.03.38.08l-1.08-4.81z"/>
                </svg>
              </a>
              <a
                href={socialLinks.google}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-primary-800 rounded-lg flex items-center justify-center text-primary-300 hover:text-white hover:bg-primary-700 transition-colors"
                aria-label="Google Reviews"
              >
                <Star className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-subheading text-sm font-semibold uppercase tracking-wider text-primary-300 mb-4">
              Practice Areas
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/landlord-tenant"
                  className="font-body text-primary-100 hover:text-white transition-colors"
                >
                  Landlord-Tenant Law
                </Link>
              </li>
              <li>
                <Link
                  to="/consumer-law"
                  className="font-body text-primary-100 hover:text-white transition-colors"
                >
                  Bankruptcy & Consumer Debt
                </Link>
              </li>
            </ul>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-subheading text-sm font-semibold uppercase tracking-wider text-primary-300 mb-4">
              Navigation
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/videos"
                  className="font-body text-primary-100 hover:text-white transition-colors"
                >
                  Videos
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="font-body text-primary-100 hover:text-white transition-colors"
                >
                  About
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="font-body text-primary-100 hover:text-white transition-colors"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-subheading text-sm font-semibold uppercase tracking-wider text-primary-300 mb-4">
              Contact
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${CONTACT_INFO.phone}`}
                  className="flex items-start gap-3 font-body text-primary-100 hover:text-white transition-colors"
                >
                  <Phone className="w-5 h-5 mt-0.5 text-primary-400" />
                  <span>{CONTACT_INFO.phoneDisplay}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="flex items-start gap-3 font-body text-primary-100 hover:text-white transition-colors"
                >
                  <Mail className="w-5 h-5 mt-0.5 text-primary-400" />
                  <span>{CONTACT_INFO.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 font-body text-primary-200">
                <MapPin className="w-5 h-5 mt-0.5 text-primary-400 flex-shrink-0" />
                <span>{CONTACT_INFO.address.street}<br />{CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</span>
              </li>
              <li className="flex items-start gap-3 font-body text-primary-200 text-sm">
                <span className="text-primary-400">Fax:</span>
                <span>(888) 768-6698</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal Section */}
        <div className="mt-16 pt-8 border-t border-primary-700">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <p className="font-body text-sm text-primary-300">
              &copy; {currentYear} Beltas Law. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="font-body text-sm text-primary-300 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="font-body text-sm text-primary-300 hover:text-white transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>

          <p className="font-body text-xs text-primary-400 leading-relaxed max-w-4xl">
            <strong>Attorney Advertising.</strong> This website is designed for general information only.
            The information presented should not be construed as formal legal advice or the formation of an
            attorney-client relationship. Results depend on individual circumstances. Prior results do not
            guarantee a similar outcome. Licensed in New York, California, and Washington D.C.
          </p>
        </div>
      </div>
    </footer>
  );
}
